import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import ProductCard from '../../../components-legacy/ProductCard';
import ProductImpression from '../../../components-legacy/ProductImpression';
import Suspense from '../../../components-legacy/Suspense';
import useGallery from '../../../hooks/useGallery';
import RouterLink from '../../../Router/Link';
import BlockSpacing from '../../../styled/BlockSpacing';
import Button from '../../../styled/Button';
import Container from '../../../styled/Container';
import Cover from '../../../styled/Cover';
import Distance from '../../../styled/Distance';
import { ProductGrid, ProductGridItem } from '../../../styled/ProductGrid';
import SliceWrapper from '../../../styled/SliceWrapper';
import clsx from 'clsx';

const Countdown = React.lazy(() => import('../../../components-legacy/Countdown'));

const Link = styled(RouterLink)`
  display: block;
`;

export interface IProductSlice extends ISlice {
  articles: IProduct[];
  countdown?: ICountdown;
  dragUp?: boolean;
  gridStyle: 'singleRowWithScroll' | 'multiRow';
  id?: string;
  link?: {
    path: string;
    title: string;
  };
  linkSetting: LinkSetting;
  title?: string;
  preamble?: string;
  titleHeadingLevel?: number;
  topImage?: {
    url: string;
    lead?: string;
    link?: {
      path: string;
      title: string;
    };
  };
}

const ProductSlice = ({
  articles,
  countdown,
  dragUp,
  gridStyle,
  id,
  link,
  linkSetting,
  preamble,
  title,
  topImage,
  titleHeadingLevel,
}: IProductSlice) => {
  const ref = useRef<HTMLUListElement>(null);

  const { hideNext, hidePrev, next, prev } = useGallery({
    container: ref,
    scrollBehavior: 'smooth',
  });

  const _link = useMemo(
    () => (link?.title && link?.path && { text: link.title, url: link.path }) || undefined,
    [link],
  );

  const image = useMemo(
    () => (topImage && { url: topImage.url, alt: topImage.lead }) || undefined,
    [topImage],
  );

  const imageLink = useMemo(
    () => (topImage?.link && { url: topImage.link.path, title: topImage.link.title }) || undefined,
    [topImage],
  );

  if (!articles?.length) {
    return null;
  }

  return (
    <BlockSpacing className={clsx(dragUp ? 'drag-up' : undefined, 'accent-wrapper')}>
      <Container yPadding="p-y-md">
        <SliceWrapper
          heading={title}
          preamble={preamble}
          link={_link}
          linkPos={linkSetting === 'LinkOverAndUnder' ? 'TopBottom' : 'Top'}
          renderLink={({ url, text }) => (
            <RouterLink Component={Button} as="a" href={url} title={text} tone="inverted">
              {text}
            </RouterLink>
          )}
          titleHeadingLevel={titleHeadingLevel}>
          {image && (
            <>
              <Cover image={image} link={imageLink} LinkComponent={Link} />
              <Distance size={['0', '2']} />
            </>
          )}
          {!!countdown && countdown.position === 'over productlist' && (
            <Suspense fallback={null}>
              <Countdown
                backgroundColor={countdown.backgroundColor}
                backgroundImage={countdown.backgroundImage?.url}
                endedMessage={countdown.endedMessage}
                endedLink={countdown.endLink?.path}
                message={countdown.message}
                link={countdown.link?.path}
                timestamp={new Date(countdown.date)}
                smaller
                standAlone
              />
              <Distance />
            </Suspense>
          )}
          <ProductGrid
            overflowScroll={gridStyle === 'singleRowWithScroll' ? 'always' : 'LtMd'}
            onClickPrev={hidePrev ? undefined : prev}
            onClickNext={hideNext ? undefined : next}
            scrollRef={ref}>
            {!!countdown && countdown.position !== 'over productlist' && (
              <ProductGridItem doubleWidth={countdown.position === 'in productlist (2 x 1)'}>
                <Suspense fallback={null}>
                  <Countdown
                    backgroundColor={countdown.backgroundColor}
                    backgroundImage={countdown.backgroundImage?.url}
                    endedMessage={countdown.endedMessage}
                    endedLink={countdown.endLink?.path}
                    message={countdown.message}
                    link={countdown.link?.path}
                    timestamp={new Date(countdown.date)}
                    smaller
                  />
                </Suspense>
              </ProductGridItem>
            )}
            {articles.map((article, idx) => (
              <ProductGridItem key={article.id}>
                <ProductImpression
                  {...article}
                  Component={ProductCard}
                  product={article as ITrackableProduct}
                  list={id}
                  position={idx + 1}
                />
              </ProductGridItem>
            ))}
          </ProductGrid>
        </SliceWrapper>
      </Container>
    </BlockSpacing>
  );
};

export default ProductSlice;
